import { useCallback, useEffect, useMemo, useState } from 'react';
import { MerlinChat, type MerlinMessage } from '@/components/MerlinChat';
import { MerlinShell } from '@/components/MerlinShell';

const STORAGE_KEY = 'merlin-sessions-v1';

type MerlinSession = {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  messages: MerlinMessage[];
  sessionId: string | null;
  jobKind: string;
  ticker: string;
  asOf: string;
};

function createSession(): MerlinSession {
  const now = Date.now();
  return {
    id: `session-${now}`,
    title: 'New chat',
    createdAt: now,
    updatedAt: now,
    messages: [],
    sessionId: null,
    jobKind: 'auto',
    ticker: '',
    asOf: '',
  };
}

function deriveTitle(messages: MerlinMessage[]) {
  const firstUser = messages.find((m) => m.role === 'user');
  if (!firstUser) return 'New chat';
  const text = firstUser.content.trim();
  if (text.length <= 42) return text;
  return `${text.slice(0, 42)}…`;
}

function loadSessions(): MerlinSession[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as MerlinSession[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveSessions(sessions: MerlinSession[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
}

function bootstrapSessions(): { sessions: MerlinSession[]; activeId: string } {
  const existing = loadSessions();
  const sessions = existing.length > 0 ? existing : [createSession()];
  return { sessions, activeId: sessions[0].id };
}

export function MerlinPage() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [{ sessions: initialSessions, activeId: initialActiveId }] = useState(bootstrapSessions);
  const [sessions, setSessions] = useState<MerlinSession[]>(initialSessions);
  const [activeSessionId, setActiveSessionId] = useState<string>(initialActiveId);

  const activeSession = useMemo(
    () => sessions.find((session) => session.id === activeSessionId) ?? sessions[0],
    [activeSessionId, sessions],
  );

  useEffect(() => {
    saveSessions(sessions);
  }, [sessions]);

  const handleNewChat = useCallback(() => {
    const session = createSession();
    setSessions((prev) => [session, ...prev]);
    setActiveSessionId(session.id);
  }, []);

  const handleSelectSession = useCallback((id: string) => {
    setActiveSessionId(id);
  }, []);

  const handleSessionChange = useCallback(
    (patch: Partial<MerlinSession>) => {
      setSessions((prev) =>
        prev.map((session) => {
          if (session.id !== activeSessionId) return session;
          const next = {
            ...session,
            ...patch,
            updatedAt: Date.now(),
          };
          if (patch.messages) {
            next.title = deriveTitle(patch.messages);
          }
          return next;
        }),
      );
    },
    [activeSessionId],
  );

  const sessionSummaries = useMemo(
    () =>
      [...sessions]
        .sort((a, b) => b.updatedAt - a.updatedAt)
        .map((session) => ({
          id: session.id,
          title: session.title,
          updatedAt: session.updatedAt,
        })),
    [sessions],
  );

  if (!activeSession) return null;

  return (
    <MerlinShell
      sessions={sessionSummaries}
      activeSessionId={activeSession.id}
      sidebarOpen={sidebarOpen}
      onToggleSidebar={() => setSidebarOpen((open) => !open)}
      onNewChat={handleNewChat}
      onSelectSession={handleSelectSession}
    >
      <MerlinChat
        key={activeSession.id}
        initialMessages={activeSession.messages}
        initialSessionId={activeSession.sessionId}
        initialJobKind={activeSession.jobKind}
        initialTicker={activeSession.ticker}
        initialAsOf={activeSession.asOf}
        onSessionChange={handleSessionChange}
      />
    </MerlinShell>
  );
}
